import { API_BASE_URL, getBaseHeaders } from './api.config';
import { throwApiError } from './api.error';
import { authenticatedCall } from './auth';

export type LegalDocumentType = 'TERMS' | 'PRIVACY' | 'COOKIES';

export interface LegalDocument {
  documentType: LegalDocumentType;
  documentVersion: string;
  title: string;
  content: string;
  contentSha256: string;
  effectiveAt: string | number;
  requiresAcceptance: boolean;
}

const activeDocumentRequests = new Map<LegalDocumentType, Promise<LegalDocument>>();

export const fetchActiveLegalDocument = (
  documentType: LegalDocumentType
): Promise<LegalDocument> => {
  const cachedRequest = activeDocumentRequests.get(documentType);
  if (cachedRequest) {
    return cachedRequest;
  }

  const request = fetch(`${API_BASE_URL}/legal-documents/${documentType}/active`, {
    method: 'GET',
    headers: getBaseHeaders(),
  }).then(async response => {
    if (!response.ok) {
      await throwApiError(response);
    }
    const document = await response.json() as LegalDocument;
    activeDocumentRequests.delete(documentType);
    return document;
  }).catch(error => {
    activeDocumentRequests.delete(documentType);
    throw error;
  });

  activeDocumentRequests.set(documentType, request);
  return request;
};

export const invalidateLegalDocumentCache = (documentType: LegalDocumentType): void => {
  activeDocumentRequests.delete(documentType);
};

export const fetchAnnouncedLegalDocument = async (noticeId: string): Promise<LegalDocument> => {
  const response = await fetch(`${API_BASE_URL}/legal-documents/notices/${encodeURIComponent(noticeId)}`, {
    headers: getBaseHeaders(),
  });
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.json();
};

export const fetchPendingLegalAcceptances = async (): Promise<LegalDocument[]> => {
  const response = await authenticatedCall(`${API_BASE_URL}/legal-documents/pending-acceptance`, {
    method: 'GET',
    headers: getBaseHeaders(),
  });
  if (!response.ok) await throwApiError(response);
  return await response.json() as LegalDocument[];
};

export const acceptLegalDocument = async (document: LegalDocument): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/legal-documents/${document.documentType}/${encodeURIComponent(document.documentVersion)}/accept`,
    { method: 'POST', headers: getBaseHeaders() }
  );
  if (!response.ok) await throwApiError(response);
};
